import * as React from "react"
import { Link, usePage } from "@inertiajs/react"
import {
  Globe,
  Inbox,
  LayoutDashboard,
  Menu,
  Palette,
  Presentation,
  Shield,
  SlidersHorizontal,
  Users,
  X,
} from "lucide-react"
import { AdminUserMenu } from "@/components/AdminUserMenu"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

export type AdminNavItem = {
  href: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  match: (url: string) => boolean
}

export type AdminNavGroup = {
  label: string
  items: AdminNavItem[]
}

// Grouped top to bottom. The labels are headings, not links — they name what
// the group is for and are never clickable.
export const ADMIN_NAV: AdminNavGroup[] = [
  {
    label: "System",
    items: [
      {
        href: "/admin/design-system",
        label: "Design System",
        icon: Palette,
        match: (url) => url.startsWith("/admin/design-system"),
      },
      {
        href: "/admin/controls",
        label: "Controls",
        icon: SlidersHorizontal,
        match: (url) => url.startsWith("/admin/controls"),
      },
      {
        href: "/admin/users",
        label: "Users",
        icon: Users,
        match: (url) => url.startsWith("/admin/users"),
      },
    ],
  },
  {
    label: "Site",
    items: [
      {
        href: "/",
        label: "Site",
        icon: Globe,
        // An ordinary in-tab visit. PublicShell keeps this rail on the public
        // pages for a signed-in admin, so going to the site no longer means
        // losing the navigation — which is the only reason this ever opened in
        // a new tab.
        match: (url) => url === "/" || url.startsWith("/?"),
      },
      {
        href: "/admin",
        label: "Metrics",
        icon: LayoutDashboard,
        // Exact, or the metrics item would light up on every /admin/* page.
        match: (url) => url === "/admin" || url === "/admin/" || url.startsWith("/admin?"),
      },
    ],
  },
  {
    label: "Audits",
    items: [
      {
        href: "/admin/audits",
        label: "Audits",
        icon: Presentation,
        match: (url) => url.startsWith("/admin/audits"),
      },
    ],
  },
  {
    label: "Leads",
    items: [
      {
        href: "/admin/leads",
        label: "Leads",
        icon: Inbox,
        match: (url) => url.startsWith("/admin/leads"),
      },
    ],
  },
]

export const SIDEBAR_WIDTH = "w-56"

// The admin area's primary navigation: a fixed black rail on the left from lg
// up, and an off-canvas drawer below that, opened from a slim bar. A 224px rail
// would take more than half a phone screen, so on small viewports it slides in
// over the content instead of permanently taking a share of it.
//
// Renders nothing for guests and non-admins. SSR-safe: it reads only shared
// props and the current url, and ThemeToggle guards `window`.
export function AdminSidebar() {
  const { props, url } = usePage<PageProps>()
  const [open, setOpen] = React.useState(false)
  const user = props.current_user

  // Any navigation closes the drawer, so it never hangs over the page someone
  // has just moved to.
  React.useEffect(() => setOpen(false), [url])

  if (!user?.admin) return null

  return (
    <>
      {/* Mobile opener. Hidden once the rail is permanent. */}
      <div className="sticky top-0 z-40 flex h-12 items-center gap-3 border-b border-hairline bg-page px-4 lg:hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open admin navigation"
          aria-expanded={open}
          className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-hairline bg-surface text-ink-body"
        >
          <Menu className="h-4 w-4" />
        </button>
        <span className="flex items-center gap-1.5 text-ink-muted">
          <Shield className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-wider">Admin</span>
        </span>
      </div>

      {/* Scrim. Only below lg, where the drawer overlays content. */}
      {open && (
        <button
          type="button"
          aria-hidden="true"
          tabIndex={-1}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-40 cursor-default bg-black/50 lg:hidden"
        />
      )}

      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col bg-chrome text-chrome-ink transition-transform duration-200 lg:translate-x-0",
          SIDEBAR_WIDTH,
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-chrome-hairline px-4">
          <span className="flex items-center gap-1.5">
            <Shield className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Admin</span>
          </span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close admin navigation"
            className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-chrome-muted hover:bg-chrome-active hover:text-chrome-ink lg:hidden"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav aria-label="Admin" className="flex-1 overflow-y-auto px-3 py-4">
          {ADMIN_NAV.map((group) => (
            <div key={group.label} className="mb-5 last:mb-0">
              {/* A heading, not a link. Nothing here navigates. */}
              <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-chrome-muted">
                {group.label}
              </p>
              <ul className="list-none space-y-0.5 p-0">
                {group.items.map((item) => (
                  <li key={item.href}>
                    <NavLink item={item} active={item.match(url)} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="shrink-0 border-t border-chrome-hairline p-3">
          <AdminUserMenu email={user.email} />
        </div>
      </div>
    </>
  )
}

function NavLink({ item, active }: { item: AdminNavItem; active: boolean }) {
  const Icon = item.icon
  const className = cn(
    "flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm no-underline transition-colors",
    active
      ? "bg-chrome-active font-medium text-chrome-ink"
      : "text-chrome-ink/75 hover:bg-chrome-active hover:text-chrome-ink",
  )

  return (
    <Link href={item.href} aria-current={active ? "page" : undefined} className={className}>
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{item.label}</span>
    </Link>
  )
}
